/**
 * Commonplace Design System — Black & White Theme
 *
 * Premium monochrome palette: black, charcoal, gray, white.
 * Signal colors retained for functional UI (success, error, warning).
 */
export const colors = {
  // ─── Primary ──────────────────────────────────────────
  primary:        '#000000',
  primaryLight:   '#2A2A2A',

  // ─── Brand accent (use sparingly) ──
  brandMaroon:    '#630E3D',
  maroon:         '#000000',
  lightMaroon:    '#2A2A2A',

  // ─── Accent ─────────────────────────────────────────────
  accent:         '#FFFFFF',
  accentMuted:    '#E0E0E0',
  darkPurple:     '#4A4A4A',
  purple:         '#6B6B6B',
  purpleLight:    '#E8E8E8',
  skyBlue:        '#63BBFF',
  skyBlueLight:   '#9DC9FF',
  sunflower:      '#EEB437',
  sunflowerLight: '#F1DB76',
  darkBlue:       '#273669',
  deepRed:        '#8B1115',
  pink:           '#F5ACB8',
  green:          '#88CD73',

  // ─── Signal ───────────────────────────────────────────
  success:        '#009B1C',
  error:          '#8B1115',
  warning:        '#EEB437',
  info:           '#63BBFF',

  // ─── Background ───────────────────────────────────────
  sand:           '#F5F5F5',
  mediumSand:     '#EBEBEB',
  deepSand:       '#D4D4D4',

  // ─── Grayscale ────────────────────────────────────────
  gray01:         '#FAFAFA',
  gray03:         '#F5F5F5',
  gray05:         '#F0F0F0',
  gray07:         '#E8E8E8',
  gray10:         '#E0E0E0',
  gray15:         '#D4D4D4',
  gray20:         '#C0C0C0',
  gray30:         '#A3A3A3',
  gray40:         '#8A8A8A',
  gray50:         '#737373',
  gray60:         '#525252',
  gray70:         '#404040',
  gray80:         '#2A2A2A',
  gray85:         '#1A1A1A',
  gray90:         '#0F0F0F',

  // ─── Core ─────────────────────────────────────────────
  white:          '#FFFFFF',
  black:          '#000000',
  dark:           '#0A0A0A',
  trueBlack:      '#000000',
  charcoal:       '#1A1A1A',
  steel:          '#2A2A2A',
} as const;

/**
 * REDESIGN palette (RD) — the warm, premium look ported from the approved
 * Claude Design mock (CommonplaceDriver.v6-final.jsx). Additive + non-breaking:
 * screens reference RD.* for the new look while legacy `colors.*` keeps working.
 * Maroon #630E3D is the brand accent; money-green for payouts, info-blue for
 * details, amber for tips/training; everything else warm monochrome.
 */
export const RD = {
  bg:           '#F6F5F3', // warm app background
  surface:      '#FFFFFF',
  ink:          '#16161A', // near-black, primary buttons + headings
  text:         '#1C1B19',
  textSec:      '#56524B',
  textMuted:    '#6E6A62',
  textFaint:    '#8C887F',
  border:       '#E4E2DD',
  borderStrong: '#CAC7C0',
  g03:          '#F1F0ED',
  g05:          '#ECEBE7',
  g07:          '#E4E2DD',
  g10:          '#D9D6D0',
  g15:          '#CAC7C0',
  g30:          '#A8A49C',
  g40:          '#8C887F',
  maroon:       '#630E3D',
  maroonSoft:   '#F4E9EF',
  money:        '#1A8A3C', // payouts / positive amounts only
  moneySoft:    '#E6F3E9',
  info:         '#2E6FB8', // informational accents
  infoSoft:     '#E7EFF7',
  amber:        '#A9760F', // tips / training
  amberSoft:    '#F7EEDA',
  danger:       '#B0342F', // destructive / error only
  dangerSoft:   '#F6E7E6',
} as const;

/** Semantic theme tokens */
export const theme = {
  primary:        colors.black,
  primaryLight:   colors.steel,
  accent:         colors.white,
  background:     colors.sand,
  surface:        colors.white,
  surfaceAlt:     colors.gray03,
  border:         colors.gray15,
  borderLight:    colors.gray07,
  text:           colors.charcoal,
  textSecondary:  colors.gray60,
  textMuted:      colors.gray50,
  textInverse:    colors.white,
  success:        colors.success,
  error:          colors.error,
  warning:        colors.warning,
} as const;

/** Font families loaded via expo-font */
export const fonts = {
  regular:        'Roobert-Regular',
  medium:         'Roobert-Medium',
  semiBold:       'Roobert-SemiBold',
  heavy:          'Roobert-Heavy',
  displayRegular: 'Reckless-Regular',
  displayBold:    'Reckless-Bold',
} as const;

/** Spacing scale (4px base) */
export const spacing = {
  xs:   4,
  sm:   8,
  md:   12,
  base: 16,
  lg:   20,
  xl:   24,
  xxl:  32,
  xxxl: 40,
} as const;

/** Border radius tokens */
export const radii = {
  sm:    8,
  md:   12,
  lg:   16,
  xl:   20,
  xxl:  24,
  pill: 28,
  full: 999,
} as const;
